import React, { useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import { Divider } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { NumericField } from '@paragon/material';
import { InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';

import { getComponentDesignGuidToQuantity } from '../../../store/selectors/componentDesignQuantitySelectors';
import {
  getDownloadRequestForSingle,
  getInitiallySelectedComponentDesignGuidToQuantityFromContext,
} from '../../../store/selectors/downloadSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CustomQuantitiesPanel } from './CustomQuantitiesPanel';
import { DownloadContextToggle } from './DownloadContextToggle';
import { DownloadListItems } from './DownloadListItems';

export type DownloadMode = 'single' | 'bulk' | 'custom';

export function DownloadsDialog() {
  const dispatch = useAppDispatch();
  const initiallySelectedComponentDesignGuidToQuantityFromContext = useAppSelector(
    getInitiallySelectedComponentDesignGuidToQuantityFromContext,
  );
  const componentDesignGuidToQuantity = useAppSelector(getComponentDesignGuidToQuantity);
  const openContextType = useAppSelector((state) => state.application.openContext?.type);
  const sharedComponentSet = useAppSelector((state) => state.sharedComponentSet.sharedComponentSet);
  const downloadRequestForSingle = useAppSelector(getDownloadRequestForSingle);

  const initiallySelectedComponentDesignGuidToQuantityFromContextEntries = Object.entries(
    initiallySelectedComponentDesignGuidToQuantityFromContext,
  );
  const exactlyOneComponentDesignSelected =
    initiallySelectedComponentDesignGuidToQuantityFromContextEntries.length === 1;
  const singularDownloadsEnabled = !sharedComponentSet && exactlyOneComponentDesignSelected;

  const [singleSelectedComponentDesignGuid, singleSelectedComponentDesignQuantity] = exactlyOneComponentDesignSelected
    ? initiallySelectedComponentDesignGuidToQuantityFromContextEntries[0]
    : [null, 0];
  let initialMode: DownloadMode;
  switch (initiallySelectedComponentDesignGuidToQuantityFromContextEntries.length) {
    case 0:
      initialMode = 'bulk';
      break;
    case 1:
      initialMode = singularDownloadsEnabled ? 'single' : 'custom';
      break;
    default:
      initialMode = 'custom';
      break;
  }
  const [mode, setMode] = useState<'single' | 'custom' | 'bulk'>(initialMode);

  // Single mode custom quantity state
  const [customQuantityForSingleDownload, setCustomQuantityForSingleDownload] = useState<number>(
    singleSelectedComponentDesignQuantity,
  );
  const isUsingCustomQuantityForSingleDownload = singleSelectedComponentDesignGuid
    ? componentDesignGuidToQuantity[singleSelectedComponentDesignGuid] !== customQuantityForSingleDownload
    : false;

  const keepDialogOpen = mode === 'custom';
  const showAppSpinner = !keepDialogOpen;
  const onBeforeDownloadBegins = keepDialogOpen ? undefined : () => void dispatch(closeCurrentPopup());

  return (
    <Dialog
      title="Download file(s) for:"
      variant="design"
      actionsNode={null}
      contentSx={[createScrollbarStyling, { mt: -1 }]}
      submitText={keepDialogOpen ? 'Done' : undefined}
    >
      <DownloadContextToggle mode={mode} onChangeMode={setMode} singularDownloadsDisabled={!singularDownloadsEnabled} />

      <Divider sx={{ my: 2 }} />

      {mode === 'single' && !singularDownloadsEnabled ? (
        <InlineNotification type="error">{`Select ${openContextType === 'station' ? 'saved ' : ''}component design(s)`}</InlineNotification>
      ) : (
        <>
          {mode === 'single' && (
            <NumericField
              fullWidth
              sx={{ mb: 2 }}
              label="Customize Quantity"
              value={customQuantityForSingleDownload}
              integers
              min={1}
              onSubmit={setCustomQuantityForSingleDownload}
              endAdornment={
                <TooltipIconButton
                  enableInteractive
                  title="Reset"
                  onClick={() => {
                    if (!singleSelectedComponentDesignGuid)
                      throw new Error('Expected exactly one component design item to be selected');
                    setCustomQuantityForSingleDownload(
                      componentDesignGuidToQuantity[singleSelectedComponentDesignGuid],
                    );
                  }}
                  disabled={!isUsingCustomQuantityForSingleDownload}
                >
                  <ClearIcon />
                </TooltipIconButton>
              }
            />
          )}

          {mode === 'custom' ? (
            <CustomQuantitiesPanel onBeforeDownloadBegins={onBeforeDownloadBegins} showAppSpinner={showAppSpinner} />
          ) : (
            <DownloadListItems
              sx={{ height: '500px', overflow: 'auto' }}
              mode={mode === 'single' && isUsingCustomQuantityForSingleDownload ? 'custom' : mode}
              customQuantities={
                mode === 'single' && isUsingCustomQuantityForSingleDownload && downloadRequestForSingle
                  ? { [Object.keys(downloadRequestForSingle.guidToQuantity)[0]]: customQuantityForSingleDownload }
                  : undefined
              }
              onBeforeDownloadBegins={onBeforeDownloadBegins}
              showAppSpinner={showAppSpinner}
            />
          )}
        </>
      )}
    </Dialog>
  );
}
